import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Receipt } from 'lucide-react';
import { errorText } from '../ErrorBanner';
import { amount } from '../../utils/format';
import { BillingItem, InvoiceBase, quoteApi } from '../../services/quoteApi';

const cell = 'w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500';
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());

/**
 * 청구 · 거래명세서(작업 10, 2026-10-22) — 재무계획의 착수금 · 중도금 · 잔금 회차마다 거래명세서를 만들어
 * 드라이브 「05.정산」 에 올린다. 회차 날짜가 다가오면 목록 위 「다가오는 청구」 에도 뜬다.
 */
export const BillingPanel: React.FC<{ projectId: number; projectName: string }> = ({ projectId, projectName }) => {
  const [base, setBase] = useState<InvoiceBase | null>(null);
  const [schedule, setSchedule] = useState<BillingItem[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<Record<number, { fileName: string; url: string }>>({});

  useEffect(() => {
    quoteApi.invoiceDraft(projectId).then((r) => { setBase(r.base); setSchedule(r.schedule); })
      .catch((e) => setError(errorText(e, '청구 회차를 불러오지 못했습니다')));
  }, [projectId]);

  const make = async (i: number) => {
    if (!base) return;
    const s = schedule[i];
    setBusy(i); setError(null);
    try {
      const r = await quoteApi.invoice(projectId, {
        ...base, date: today(),
        items: [{ date: today(), name: projectName, qty: 1, unit: s.amount, note: `${s.name} ${Math.round(s.ratio * 100)}%` }],
      });
      setMade({ ...made, [i]: r });
    } catch (e) { setError(errorText(e, '거래명세서를 만들지 못했습니다')); }
    finally { setBusy(null); }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">청구 · 거래명세서</h2>
        <span className="text-xs text-slate-400">재무계획의 회차마다 만들어 드라이브 「05.정산」 에 올립니다</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        {!base ? <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> 불러오는 중...</p> : (
          <>
            <div className="grid gap-2 sm:grid-cols-[1fr_2fr_1fr_auto] items-end">
              <label className="text-xs text-slate-500">공급받는자<input value={base.client} onChange={(e) => setBase({ ...base, client: e.target.value })} className={`mt-1 ${cell}`} /></label>
              <label className="text-xs text-slate-500">주소<input value={base.address} onChange={(e) => setBase({ ...base, address: e.target.value })} className={`mt-1 ${cell}`} placeholder="공급받는자 주소" /></label>
              <label className="text-xs text-slate-500">전화 · 담당<input value={base.contact} onChange={(e) => setBase({ ...base, contact: e.target.value })} className={`mt-1 ${cell}`} /></label>
              <span className="text-xs text-slate-400 pb-1.5">금액은 VAT 포함</span>
            </div>
            {schedule.length === 0 ? (
              <p className="text-sm text-slate-500">재무계획에 착수금 · 중도금 · 잔금 회차가 없습니다. 재무계획 칸에서 회차를 잡으면 여기에 뜹니다.</p>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="text-left font-medium py-1.5">회차</th><th className="text-left font-medium">예정일</th>
                  <th className="text-right font-medium">금액(원, VAT 포함)</th><th className="w-56" />
                </tr></thead>
                <tbody>
                  {schedule.map((s, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2">{s.name} <span className="text-xs text-slate-400">{Math.round(s.ratio * 100)}%</span></td>
                      <td className={s.dueDate && s.dueDate < today() ? 'text-red-600' : ''}>{s.dueDate || '—'}</td>
                      <td className="text-right tabular-nums">{amount(s.amount)}</td>
                      <td className="text-right">
                        {made[i] ? (
                          <a href={made[i].url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-0.5">만듦 · 열기 <ExternalLink size={11} /></a>
                        ) : (
                          <button onClick={() => make(i)} disabled={busy !== null || s.amount <= 0}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1">
                            {busy === i ? <Loader2 size={12} className="animate-spin" /> : <Receipt size={12} />} 거래명세서 만들기
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
